/**
 * Page loads judged by what the browser already holds (ADR 0061).
 *
 * A hard refresh, a first visit and the first load after a release fetch the
 * application code from the network. A normal reload takes it from the browser
 * cache. The browser reports both as a reload, so the entry script's own
 * resource timing tells them apart.
 */
import { useLayoutEffect, useRef } from 'react';
import { hasAnyGoldenInteractionMeasure, startGoldenInteraction } from './goldenInteraction';

/**
 * Whether this document's code came from the network: true for a hard refresh
 * or a first visit, false when the browser cache served it, null when unknown.
 */
export function documentCodeFromNetwork(): boolean | null {
  const source = globalThis.document?.querySelector<HTMLScriptElement>(
    'script[type="module"][src]'
  )?.src;
  if (!source || typeof globalThis.performance?.getEntriesByName !== 'function') return null;
  const timing = globalThis.performance.getEntriesByName(source)[0] as
    PerformanceResourceTiming | undefined;
  if (timing === undefined || !(timing.encodedBodySize > 0)) return null;
  // A cache hit transfers nothing, a revalidation only headers; a download
  // transfers at least the body.
  return timing.transferSize >= timing.encodedBodySize;
}

/**
 * The login form of a document load, from navigation start to the frame that
 * shows it ready for input. `shown` is true once the form renders.
 */
export function useLoginScreenLoad(shown: boolean): void {
  const measured = useRef(false);
  useLayoutEffect(() => {
    if (!shown || measured.current) return;
    measured.current = true;
    // A later route to /login (logout, an expired session) is not a page load.
    if (hasAnyGoldenInteractionMeasure()) return;
    const fromNetwork = documentCodeFromNetwork();
    if (fromNetwork === null) return;
    const interaction = startGoldenInteraction(
      fromNetwork ? 'login-screen-uncached-load' : 'login-screen-cached-load',
      { actionStartTimeMs: 0 }
    );
    interaction.markFrontendWorkStart();
    interaction.markFrontendWorkComplete();
    void interaction.complete('completion');
  }, [shown]);
}
